import { useEffect, useRef } from 'react';
import type { OAuthProvider } from '../../api';
import { useTheme } from '../../context/ThemeContext';

const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID as string | undefined;

const oauthAvailable = Boolean(GOOGLE_CLIENT_ID);

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    google?: any;
  }
}

function loadScript(src: string, id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    if (document.getElementById(id)) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = src;
    script.id = id;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.head.appendChild(script);
  });
}

interface Props {
  /** Receives the provider and its token (Google ID token). */
  onToken: (provider: OAuthProvider, token: string) => void;
  disabled?: boolean;
}

export default function OAuthButtons({ onToken, disabled }: Props) {
  const { resolved } = useTheme();
  const googleRef = useRef<HTMLDivElement>(null);
  const onTokenRef = useRef(onToken);
  onTokenRef.current = onToken;

  // Re-runs on theme change: Google renders the button imperatively, so the
  // only way to restyle it is to render it again.
  useEffect(() => {
    if (!GOOGLE_CLIENT_ID) return;
    let cancelled = false;
    loadScript('https://accounts.google.com/gsi/client', 'gsi-script')
      .then(() => {
        if (cancelled || !window.google || !googleRef.current) return;
        // renderButton appends rather than replaces — without clearing, a
        // theme change stacks a second button under the first.
        googleRef.current.innerHTML = '';
        window.google.accounts.id.initialize({
          client_id: GOOGLE_CLIENT_ID,
          callback: (resp: { credential: string }) => onTokenRef.current('google', resp.credential),
        });
        window.google.accounts.id.renderButton(googleRef.current, {
          theme: resolved === 'dark' ? 'filled_black' : 'outline',
          size: 'large',
          width: 300,
          text: 'continue_with',
        });
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [resolved]);

  if (!oauthAvailable) return null;

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <div className="bg-muted-strong h-px flex-1" />
        <span className="text-fg-subtle text-xs">or</span>
        <div className="bg-muted-strong h-px flex-1" />
      </div>
      {GOOGLE_CLIENT_ID && (
        // `scheme-light` is load-bearing. Google renders the button in a
        // cross-origin iframe, and per CSS Color Adjust the browser replaces an
        // iframe's transparent canvas with an opaque one whenever the embedder's
        // color scheme differs from the embedded document's. Under `.dark` —
        // which sets `color-scheme: dark` — that painted a white slab behind the
        // button. Matching Google's light-only scheme keeps the canvas
        // transparent; `filled_black` above still supplies the dark styling.
        <div
          ref={googleRef}
          className={`flex justify-center scheme-light ${disabled ? 'pointer-events-none opacity-50' : ''}`}
        />
      )}
    </div>
  );
}
